/* ============================================================================
 *  API adapters — Mentoring Program (owner side)
 * ----------------------------------------------------------------------------
 *  Normalise the mentoring API payloads (core-platform `Modules/Mentoring`,
 *  branch feat/mentoring-program-gaps) into the shapes the views were built
 *  against: nested duration objects → flat value/unit fields, flat banner
 *  fields → banner.{url,type}, flat dates → a timestamps object.
 * ========================================================================== */

/**
 * MentoringProgramResource → view shape. The ApiDog `MentoringProgram` schema
 * is flat duration fields + a nested banner object; the fallbacks tolerate the
 * earlier nested-duration/flat-banner build during rollout.
 */
export function adaptProgram(raw) {
  if (!raw) return null;

  return {
    id: raw.id,
    community_id: raw.community_id,
    name: raw.name,
    description: raw.description ?? "",
    active: !!raw.active,
    duration_value: raw.duration_value ?? raw.duration?.value ?? null,
    duration_unit: raw.duration_unit ?? raw.duration?.unit ?? null,
    split_duration_value:
      raw.split_duration_value ?? raw.split_duration?.value ?? null,
    split_duration_unit:
      raw.split_duration_unit ?? raw.split_duration?.unit ?? null,
    plan_id: raw.plan_id ?? null,
    split_percentage: raw.split_percentage ?? null,
    contract_url: raw.contract_url ?? null,
    application: raw.application ?? null,
    banner: raw.banner
      ? { url: raw.banner.url ?? null, type: raw.banner.type ?? "image" }
      : { url: raw.banner_url ?? null, type: raw.banner_type ?? "image" },
    // { active_enrollments, total_members, total_revenue_cents } — null on
    // create/update responses (only the list/show queries compute them).
    enrollments_count: raw.enrollments_count ?? null,
    metrics: raw.metrics ?? null,
    timestamps: {
      created_at: raw.timestamps?.created_at ?? raw.timestamps?.created ?? null,
      updated_at: raw.timestamps?.updated_at ?? raw.timestamps?.updated ?? null,
    },
  };
}

/**
 * MentoringEnrollmentListItem (ApiDog schema) → enrollments-table row.
 * creator is null while an invite hasn't been accepted (rendered "Convidado");
 * members_count/revenue_cents live inside community; dates are grouped under
 * timestamps. Near-passthrough by design.
 */
export function adaptEnrollmentListItem(raw, programId) {
  const creator = raw.creator ?? null;

  return {
    id: raw.id,
    program_id: raw.program_id ?? programId,
    status: raw.status,
    source: raw.source,
    invited_email: raw.invited_email ?? null,
    split_percentage: raw.split_percentage ?? null,
    effective_split_percentage: raw.effective_split_percentage ?? null,
    creator: creator
      ? {
          name: creator.name,
          email: creator.email ?? raw.invited_email ?? null,
          avatar_url: creator.avatar_url ?? null,
          phone: creator.phone ?? null,
        }
      : null,
    community: raw.community
      ? {
          id: raw.community.id,
          name: raw.community.name,
          banner_url: raw.community.banner_url ?? null,
          members_count: raw.community.members_count ?? 0,
          revenue_cents: raw.community.revenue_cents ?? 0,
        }
      : null,
    form_submission_id: raw.form_submission_id ?? null,
    timestamps: {
      created_at: raw.timestamps?.created_at ?? null,
      started_at: raw.timestamps?.started_at ?? null,
      ends_at: raw.timestamps?.ends_at ?? null,
      ended_at: raw.timestamps?.ended_at ?? null,
      split_ends_at: raw.timestamps?.split_ends_at ?? null,
      split_ended_at: raw.timestamps?.split_ended_at ?? null,
    },
  };
}

/**
 * MentoringEnrollmentDetail (ApiDog schema) → detail view shape. The payload
 * is flat (no `enrollment` wrapper), embeds the program, and the insights
 * summary is already view-shaped — this is a near-passthrough that adapts the
 * embedded program and fills defaults.
 */
export function adaptEnrollmentDetail(raw) {
  if (!raw?.id) return null;

  return {
    id: raw.id,
    program_id: raw.program_id ?? raw.program?.id ?? null,
    community_id: raw.community_id ?? null,
    user_id: raw.user_id ?? null,
    status: raw.status,
    source: raw.source,
    invited_email: raw.invited_email,
    split_percentage: raw.split_percentage ?? null,
    effective_split_percentage: raw.effective_split_percentage ?? null,
    notes: raw.notes ?? null,
    reviewed_by_id: raw.reviewed_by_id ?? null,
    // Already view-shaped: { id, name, email, phone, avatar_url } and
    // { id, name, description, banner_url, members_count, plans }.
    creator: raw.creator ?? null,
    community: raw.community ?? null,
    form_submission_id: raw.form_submission_id ?? null,
    premature_end: !!raw.premature_end,
    program: adaptProgram(raw.program),
    insights: raw.insights
      ? {
          total_revenue_cents: raw.insights.total_revenue_cents ?? 0,
          total_orders: raw.insights.total_orders ?? 0,
          new_members: raw.insights.new_members ?? null,
          timestamps: {
            date_from: raw.insights.timestamps?.date_from ?? null,
            date_to: raw.insights.timestamps?.date_to ?? null,
          },
        }
      : null,
    timestamps: {
      created_at: raw.timestamps?.created_at ?? null,
      updated_at: raw.timestamps?.updated_at ?? null,
      reviewed_at: raw.timestamps?.reviewed_at ?? null,
      started_at: raw.timestamps?.started_at ?? null,
      ends_at: raw.timestamps?.ends_at ?? null,
      ended_at: raw.timestamps?.ended_at ?? null,
      split_ends_at: raw.timestamps?.split_ends_at ?? null,
      split_ended_at: raw.timestamps?.split_ended_at ?? null,
      invite_token_expires_at: raw.timestamps?.invite_token_expires_at ?? null,
    },
  };
}
